import React, {useCallback, useMemo, useState} from 'react';

import clsx from 'clsx';

import {QueryArea as KITQueryArea} from '@pb/kit-component-query-area-v2';

import {QueryPill} from './query-pill';

import {queryAreaInvalid, queryAreaValid} from './query-area.module.styl';


import type {ItemData, QueryPillProps} from '@pb/kit-component-query-area-v2';
import type {ClipboardEvent} from 'react';
import type {Pill} from 'src/typedef';

type Props = {
    items: Array<Pill<number | string>>,
    onAdd: (items: Array<Pill<string>>) => void,
    onRemove: (item: string) => void,
    placeholder?: string,
    onValidate: (value: string, itemsLength: number) => boolean,
    required?: boolean,
    maxSelected?: number,
    customSeparator?: string | RegExp,

    onChangeQueryCallback?: (value: string) => void,
    onFocusCallback?: () => void,
    onBlurCallback?: () => void,
};

const getPillsLength = (pills: Array<ItemData>, variant: 'DEFAULT' | 'ADDITION' | 'EXCLUSION' | undefined) =>
    pills.filter(pill => variant === pill.variant).length;

export const QueryArea = (props: Props) => {
    const {
        placeholder,
        required,
        onValidate,
        onAdd,
        onRemove,
        items,
        maxSelected,
        customSeparator,
        onChangeQueryCallback,
        onFocusCallback,
        onBlurCallback,
    } = props;

    const [searchQuery, setSearchQuery] = useState('');

    const pills = useMemo(() => items.map(({value, invalid}) => {
        const stringValue = value.toString();

        return {
            id: stringValue,
            name: stringValue,
            variant: invalid ? 'EXCLUSION' : 'DEFAULT' as 'DEFAULT' | 'EXCLUSION',
        };
    }), [items]);

    const classes = clsx([
        getPillsLength(pills, 'ADDITION') && !getPillsLength(pills, 'EXCLUSION') && queryAreaValid,
        (required
            ? getPillsLength(pills, 'EXCLUSION') || !pills.length
            : getPillsLength(pills, 'EXCLUSION')
        ) && queryAreaInvalid,
    ]);

    const addItems = useCallback((rawValue: string) => {
        const parsedData = [...new Set(rawValue
            .split(customSeparator || ',')
            .map(id => id.trim())
            .filter(id => id),
        )];

        const addedItems: Array<Pill<string>> = [];

        parsedData.forEach(queriedItem => {
            if (maxSelected && pills.length + addedItems.length >= maxSelected) {
                return;
            }

            if (pills.some(pill => pill.name === queriedItem)) {
                return;
            }

            addedItems.push({value: queriedItem, invalid: !onValidate(queriedItem, parsedData.length + items.length)});
        });

        if (addedItems.length) {
            onAdd(addedItems);
        }

        setSearchQuery('');
        if (onChangeQueryCallback) {
            onChangeQueryCallback('');
        }
    }, [pills, customSeparator, maxSelected, onValidate, onAdd, items.length, onChangeQueryCallback]);

    const onAddCallback = useCallback((item: ItemData) => {
        addItems(item.name);
    }, [addItems]);

    const onRemoveCallback = useCallback((item: ItemData) => {
        onRemove(item.name);
    }, [onRemove]);

    const onFocus = useCallback(() => {
        if (onFocusCallback) {
            onFocusCallback();
        }
    }, [onFocusCallback]);

    const onBlur = useCallback(() => {
        if (onBlurCallback) {
            onBlurCallback();
        }
    }, [onBlurCallback]);

    const onPaste = (e: ClipboardEvent) => {
        const text = e.clipboardData.getData('text');
        if (text) {
            e.preventDefault();
            addItems(text.replace(/[\n;]/g, ','));
        }
    };

    return (
        <div className={classes}>
            <KITQueryArea
                className="ellipsis"
                placeholder={placeholder}
                data={pills}
                value={searchQuery}
                onChange={e => {
                    setSearchQuery(e.target.value);
                    if (onChangeQueryCallback) {
                        onChangeQueryCallback(e.target.value);
                    }
                }}
                onAdd={onAddCallback}
                onRemove={onRemoveCallback}
                onFocus={onFocus}
                onBlur={onBlur}
                onPaste={onPaste}
                renderPill={({item, onRemove: onRemoveItem}: QueryPillProps) => (
                    <QueryPill key={item.name} item={item} onRemove={onRemoveItem}/>
                )}
            />
        </div>
    );
};
